import moment from 'moment'
import _ from 'lodash'
import * as d3 from 'd3'
import d3Tip from 'd3-tip'
import { AVM_TUNE_TYPES, AVM_SOURCE_TYPES } from 'src/constants/valuation'
import linkSrc from 'assets/homes/img/tune-your-value-icon.svg'

class ValueLineChart {
  constructor(el, path) {
    this.el = el
    this._buildSvg()
    this._buildChartContainer()
    this._setupResize()
    this._setupTips()
    this.clipPath = path
  }

  update(data) {
    this.data = data ? this._prepData(data) : this.data
    this._combineValuations()
    this._draw()
    if (data.disableValue) {
      this.tip.attr('class', 'hidden')
    }
  }

  destroy() {
    // ensure all event listeners are cleaned up
    if (typeof window !== 'undefined')
      window.removeEventListener('resize', this._redrawChart)
  }

  _draw() {
    this._calcDimensions()
    this._shiftChartContainer()
    this._calcScales()
    this._axes()
    this._addPhotoCropper()
    this._segmentConnectors()
    this._segments()
  }

  _buildSvg() {
    this.svg = d3.select(this.el)
  }

  _buildChartContainer() {
    this.chart = this.svg.append('g')
  }

  _shiftChartContainer() {
    this.chart.attr(
      'transform',
      `translate(${this.margin.left},${this.margin.top})`
    )
  }

  _axes() {
    this._drawXAxis()
    this._drawYAxis()
  }

  _segments() {
    const line = d3
      .line()
      .curve(d3.curveCardinal)
      .x(d => this.scaleX(d.date))
      .y((d, i) => this.scaleY(d.valuation))

    this.segment = this.chart
      .selectAll('.segment')
      .data(this.data.segments)
      .enter()
      .append('g')
      .attr('class', 'segment')

    this.segment
      .filter(d => d.valuations.length > 1)
      .append('path')
      .attr('class', 'line')
      .attr('d', d => line(d.valuations))

    this.segment
      .selectAll('g')
      .data(d => d.valuations)
      .enter()
      .append('g')
      .classed('datapoint', true)
      .call(parentDatapoint => {
        this._addDataPoints(parentDatapoint)
      })
  }

  _segmentConnectors() {
    const line = d3
      .line()
      .x(d => this.scaleX(d.date))
      .y((d, i) => this.scaleY(d.valuation))

    this.connector = this.chart
      .selectAll('.line--connector')
      .data(this.data.connectors.filter(d => d.valuations.every(Boolean)))
      .enter()
      .append('path')
      .attr('class', 'line line--connector')
      .attr('d', d => line(d.valuations))
  }

  _addDataPoints(datapoint) {
    this._photoPosts(datapoint)
    this._pointBorders(datapoint)
    this._points(datapoint)
    this._photoBorders(datapoint)
    this._photos(datapoint)
    this._wrenchFills(datapoint)
    this._wrenches(datapoint)
  }

  _photoPosts(datapoint) {
    datapoint
      .filter(
        (d, i) =>
          i === 0 &&
          d.source &&
          Object.values(AVM_TUNE_TYPES).includes(d.type) &&
          (d.source.type === AVM_SOURCE_TYPES.CLIENT || d.source.photoUrl)
      )
      .append('line')
      .classed('photo-post', true)
      .attr('x1', d => this.scaleX(d.date))
      .attr('y1', d => this.scaleY(d.valuation))
      .attr('x2', d => this.scaleX(d.date))
      .attr(
        'y2',
        d => this.scaleY(d.valuation) + this._getPhotoOffset(d.valuation)
      )
  }

  _points(datapoint) {
    datapoint
      .append('circle')
      .classed('point', true)
      .attr('cx', d => this.scaleX(d.date))
      .attr('cy', d => this.scaleY(d.valuation))
      .attr('r', 3.5)
      .on('mouseover', this.tip.show)
      .on('mouseout', this.tip.hide)
  }

  _pointBorders(datapoint) {
    datapoint
      .filter((d, i, nodes) => i === 0 || i === nodes.length - 1)
      .append('circle')
      .classed('point-border', true)
      .attr('cx', d => this.scaleX(d.date))
      .attr('cy', d => this.scaleY(d.valuation))
      .attr('r', 7.5)
  }

  _photoBorders(datapoint) {
    datapoint
      .filter(
        (d, i) =>
          i === 0 &&
          d.source &&
          Object.values(AVM_TUNE_TYPES).includes(d.type) &&
          d.source.photoUrl
      )
      .append('circle')
      .classed('photo-border', true)
      .attr('r', this._getResponsivePhotoWidth() / 2)
      .attr('transform', d => {
        const x = this.scaleX(d.date)
        const y =
          this.scaleY(d.valuation) + this._getPhotoOffset(d.valuation, 'middle')
        return `translate(${x},${y})`
      })
  }

  _photos(datapoint) {
    const urlBase = this.clipPath || window.location.href

    datapoint
      .filter(
        (d, i, a) =>
          i === 0 &&
          d.source &&
          Object.values(AVM_TUNE_TYPES).includes(d.type) &&
          d.source.photoUrl
      )
      .append('image')
      .classed('photo', true)
      .attr('xlink:href', d => d.source.photoUrl)
      .attr('width', this._getResponsivePhotoWidth())
      .attr('height', this._getResponsivePhotoWidth())
      .attr('transform', d => {
        const x = this.scaleX(d.date) - this._getResponsivePhotoWidth() / 2
        const y =
          this.scaleY(d.valuation) + this._getPhotoOffset(d.valuation, 'top')
        return `translate(${x},${y})`
      })
      .attr('preserveAspectRatio', 'xMaxYMin slice')
      .style('clip-path', `url(${urlBase}#photoCircleCrop)`) // http://stackoverflow.com/a/18265336/580366
  }

  _wrenchFills(datapoint) {
    datapoint
      .filter(
        (d, i) =>
          i === 0 &&
          d.source &&
          d.type === AVM_TUNE_TYPES.TYV &&
          d.source.type === AVM_SOURCE_TYPES.CLIENT
      )
      .append('circle')
      .classed('wrench-fill', true)
      .attr('r', this._getResponsivePhotoWidth() / 2)
      .attr('transform', d => {
        const x = this.scaleX(d.date)
        const y =
          this.scaleY(d.valuation) + this._getPhotoOffset(d.valuation, 'middle')
        return `translate(${x},${y})`
      })
  }

  _wrenches(datapoint) {
    const urlBase = this.clipPath || window.location.href

    datapoint
      .filter(
        (d, i, a) =>
          i === 0 &&
          d.source &&
          d.type === AVM_TUNE_TYPES.TYV &&
          d.source.type === 'client'
      )
      .append('image')
      .classed('icon', true)
      .attr('width', this._getResponsivePhotoWidth() / 2)
      .attr('height', this._getResponsivePhotoWidth() / 2)
      .attr('xlink:href', linkSrc)
      .attr('transform', d => {
        const x = this.scaleX(d.date) - this._getResponsivePhotoWidth() / 4
        const y =
          this.scaleY(d.valuation) +
          this._getPhotoOffset(d.valuation, 'top') +
          this._getResponsivePhotoWidth() / 4
        return `translate(${x},${y})`
      })
      .attr('preserveAspectRatio', 'xMaxYMin slice')
      .style('clip-path', `url(${urlBase}#photoCircleCrop)`) // http://stackoverflow.com/a/18265336/580366
  }

  _drawXAxis() {
    const years = moment(this.xDomain[1]).diff(this.xDomain[0], 'years')
    const valuationCount = this.allValuations.length

    const xAxis = d3
      .axisBottom(this.scaleX)
      .tickFormat(d3.timeFormat(years > 0 ? "%b '%y" : '%b'))

    if (years > 0) {
      // Reduced room with larger labels specified above.
      xAxis.ticks(6)
    } else {
      xAxis.ticks(valuationCount)
    }

    this.chart
      .append('g')
      .attr('class', 'axis axis--x')
      .attr(
        'transform',
        `translate(0,${this.chartHeight - this.margin.bottom / 2 + 5})`
      )
      .call(xAxis)
  }

  _drawYAxis() {
    if (this.data.disableValue) return
    const yAxis = d3
      .axisRight(this.scaleY)
      .tickSize(this.outerWidth)
      .tickArguments([3])
      .tickFormat(d3.format('.2s'))

    // Add to outer svg because it's outside the graph on the left
    // Prepend instead of append so that it's under the chart
    this.yAxis = this.svg
      .insert('g', ':first-child')
      .attr('class', 'axis axis--y')
      .attr('transform', `translate(0, ${this.margin.top})`)
      .call(yAxis)

    this.yAxis.selectAll('text').attr('x', 0).attr('dy', -6)
  }

  _addPhotoCropper() {
    const halfPhotoWidth = this._getResponsivePhotoWidth() / 2
    this.svg
      .append('defs')
      .append('clipPath')
      .attr('id', 'photoCircleCrop')
      .append('circle')
      .attr('r', halfPhotoWidth)
      .attr('cx', halfPhotoWidth)
      .attr('cy', halfPhotoWidth)
  }

  _setupTips() {
    // Lame hack for tests to run. We'll need to dev our own tooltip at some point.
    if (!d3Tip) {
      this.tip = { show: () => {}, hide: () => {} }
      return
    }

    if (!this.tip) {
      const id = _.uniqueId('valueLineChart')

      this.tip = d3Tip()
        .attr('class', 'chart-tip')
        .attr('id', id)
        .offset([-18, 0])
        .html(
          d =>
            `<em>$${d.valuation.toLocaleString()}</em>${moment(d.date).format(
              'MMM YYYY'
            )}`
        )

      this.chart.call(this.tip)
      // Move tooltip into themeContainer for custom css var access
      const fragment = document.createDocumentFragment()

      fragment.appendChild(document.getElementById(id))
      document.getElementById('themeContainer').appendChild(fragment)
    }
  }

  _calcDimensions() {
    this.margin = { top: 0, right: 20, bottom: 30, left: 60 }
    this.outerWidth = this.el.getBoundingClientRect().width
    this.outerHeight = this.el.getBoundingClientRect().height
    this.chartWidth = this.outerWidth - this.margin.left - this.margin.right
    this.chartHeight = this.outerHeight - this.margin.top - this.margin.bottom
  }

  _calcScales() {
    this.scaleX = d3
      .scaleTime()
      .domain(this.xDomain)
      .range([0, this.chartWidth])
    this.scaleY = d3
      .scaleLinear()
      .domain(this._getPaddedYDomain())
      .range([this.chartHeight, 0])
  }

  _combineValuations() {
    const data = _.cloneDeep(this.data)
    let valuations = []
    data.segments.forEach(
      segment => (valuations = valuations.concat(segment.valuations))
    )
    this.allValuations = valuations
  }

  _prepData(data) {
    let transformedData = _.cloneDeep(data)
    transformedData = this._setupSourceAndDates(transformedData)
    transformedData.connectors = this._connectorData(transformedData)

    return transformedData
  }

  _setupSourceAndDates(data) {
    const transformedData = _.cloneDeep(data)
    const parseTime = d3.timeParse('%Y-%m')

    transformedData.segments.forEach((segment, i) => {
      segment.valuations = segment.valuations.map(valuation => {
        valuation.source = segment.source
        valuation.date = parseTime(this._setDateToFirstOfMonth(valuation.date))
        return valuation
      })
    })

    return transformedData
  }

  _connectorData(data) {
    const connectors = []

    data.segments.forEach((segment, i) => {
      if (i > 0) {
        const connector = {}
        const previousSegment = data.segments[i - 1]
        connector.valuations = [
          previousSegment.valuations[previousSegment.valuations.length - 1],
          segment.valuations[0],
        ]
        connectors.push(connector)
      }
    })

    return connectors
  }

  _setDateToFirstOfMonth(date) {
    return moment(date).format('YYYY-MM')
  }

  get xDomain() {
    return d3.extent(this.allValuations, d => d.date)
  }

  _getPaddedYDomain() {
    let min = d3.min(this.allValuations, d => d.valuation)
    let max = d3.max(this.allValuations, d => d.valuation)

    // give the chart some breathing room, make it less dramatic
    min -= min * 0.1
    max += max * 0.05

    return [min, max]
  }

  _getPhotoOffset(value, origin) {
    const PHOTO_WIDTH = this._getResponsivePhotoWidth()
    let offset = PHOTO_WIDTH * 1.333

    if (origin === 'middle') offset += PHOTO_WIDTH / 2

    if (this.scaleY(value) > this.chartHeight / 2) {
      if (origin === 'top') offset += PHOTO_WIDTH

      return -offset
    }

    return offset
  }

  _getResponsivePhotoWidth() {
    if (this._isSmallSize()) return 20

    return 26
  }

  _isSmallSize() {
    return this.outerWidth < 450
  }

  _setupResize() {
    if (this._redrawChart) return

    this._redrawChart = _.debounce(
      () => {
        this.chart.selectAll('*').remove()
        if (this.yAxis) {
          this.yAxis.remove()
        }
        this._draw()
      },
      250,
      { leading: false }
    )

    if (typeof window !== 'undefined')
      window.addEventListener('resize', this._redrawChart)
  }
}

export default ValueLineChart
